import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import InlineSvg from './InlineSvg'
import HeaderButton from './HeaderButton'
import BuyButton from './BuyButton'
import MinusButton from './MinusButton'
import PlusButton from './PlusButton'
import { t } from '../utils/i18n'
import { getTranslation, getVimeoId, getBaseModel } from '../utils/bike-schema'
import { isDanish } from '../utils/url'
import Modal from './Modal'
import {
  baseModelClicked,
  presetsClicked,
  videoClicked,
  fullPaintClicked,
  inquiryClicked,
  shareClicked,
  undoClicked,
  bikeImageReload,
  nextColorClicked,
  closePopover,
  settingsClicked
} from '../actions'
import { bikeConfigToQuery, getOrderStatement } from '../utils/bike-config'
import { BACKEND_URL } from '../utils/settings'

export const handleUndo = (
  undoClicked,
  bikeImageReload,
  fullHistory,
  undoCounter,
  undoed
) => {
  const currentPos = undoed ? undoCounter + 2 : undoCounter + 1
  const choicesToApply = fullHistory[fullHistory.length - currentPos]

  if (fullHistory.length >= currentPos) {
    undoClicked(choicesToApply)
    bikeImageReload()
  }
}

export const handleNextColor = (
  next,
  nextColorClicked,
  bikeImageReload,
  fullPaintPopoverOpen
) => {
  nextColorClicked(next, fullPaintPopoverOpen)
  bikeImageReload()
}

export const handleRedo = (
  undoClicked,
  bikeImageReload,
  fullHistory,
  undoCounter
) => {
  const choicesToApply = fullHistory[fullHistory.length - undoCounter]
  if (undoCounter > 0) {
    undoClicked(choicesToApply, true)
    bikeImageReload()
  }
}

const Header = () => {
  const dispatch = useDispatch()

  const currencyCode = useSelector((state) => state.currencyCode)
  const quantity = useSelector((state) => state.quantity)
  const bikeConfig = useSelector((state) => state.bikeConfig)
  const fullHistory = useSelector((state) => state.fullHistory)
  const undoCounter = useSelector((state) => state.undoCounter)
  const undoed = useSelector((state) => state.undoed)
  const disclaimer = useSelector((state) => state.disclaimer)

  const baseModel = getBaseModel(bikeConfig.baseModelId)
  const statement = getOrderStatement(bikeConfig, quantity, currencyCode)
  const query = bikeConfigToQuery(bikeConfig)
  const downloadUrl = `${BACKEND_URL}/bike-image?download=1&${query}`
  const pdfUrl = `${BACKEND_URL}/bike-pdf?${query}&currency=${currencyCode}`

  const [show, setShow] = useState(false)
  const isCargo = bikeConfig.baseModelId.includes('cargo')
  const baseModelToolclassName = [
    'velorbis-customizer-header-tool',
    'velorbis-customizer-flex-1',
    'velorbis-customizer-header-base-model'
  ].join(' ')

  useEffect(() => {
    dispatch(closePopover())
  }, [dispatch])

  const handleBaseModelClick = () => {
    dispatch(baseModelClicked())
  }

  return (
    <>
      {show && !disclaimer && <Modal />}
      <div className="velorbis-customizer-header hide-768">
        <div className="velorbis-customizer-header-group velorbis-customizer-specifications-group">
          <div className="velorbis-customizer-specifications-join-group">
            <div
              className={baseModelToolclassName}
              onClick={handleBaseModelClick}
              title={t('base_models_title')}
            >
              <div className="velorbis-customizer-header-tool-inner">
                <div className="velorbis-customizer-flex-1">
                  {getTranslation(baseModel.name)}
                </div>
                <span>&#x25BE;</span>
              </div>
            </div>

            <div className="velorbis-customizer-header-group hide-768">
              {getVimeoId(baseModel.id) && isDanish && (
                <div
                  className="velorbis-customizer-header-tool velorbis-customizer-header-video"
                  onClick={() => dispatch(videoClicked())}
                  title="Video"
                >
                  <div className="velorbis-customizer-header-tool-inner">
                    <InlineSvg className="youtube-icon" src="icons/youtube" />
                  </div>
                </div>
              )}

              <div
                className="velorbis-customizer-header-tool velorbis-customizer-header-presets"
                onClick={() => dispatch(presetsClicked())}
                title={t(isCargo ? 'select_angle' : 'presets_title')}
              >
                <div className="velorbis-customizer-header-tool-inner">
                  <InlineSvg src="icons/grid" />
                </div>
              </div>
              {!bikeConfig.baseModelId.includes('cargo') && (
                <div
                  className="velorbis-customizer-header-tool velorbis-customizer-header-full-paint"
                  onClick={() => dispatch(fullPaintClicked())}
                  title={t('full_paint_title')}
                >
                  <div className="velorbis-customizer-header-tool-inner">
                    <InlineSvg src="icons/paintbrush" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="hide-768">
            <UndoButton
              onClick={() =>
                handleUndo(
                  (id) => dispatch(undoClicked(id)),
                  () => dispatch(bikeImageReload()),
                  fullHistory,
                  undoCounter,
                  undoed
                )
              }
            />
            <RedoButton
              onClick={() =>
                handleRedo(
                  (id) => dispatch(undoClicked(id)),
                  () => dispatch(bikeImageReload()),
                  fullHistory,
                  undoCounter,
                  undoed
                )
              }
            />
          </div>
        </div>
        <div className="velorbis-customizer-header-fill" />
        <div className="velorbis-customizer-header-join-group hide-768">
          <div className="velorbis-customizer-header-group">
            <div className="velorbis-customizer-header-add-to-cart-desktop">
              <BuyButton
                currencyCode={currencyCode}
                totalPrice={statement.unitPrice}
                baseModelId={baseModel.id}
              />
            </div>
            <div
              className="velorbis-customizer-header-tool velorbis-customizer-header-inquiry velorbis-customizer-header-inquiry-desktop"
              onClick={() => dispatch(inquiryClicked())}
            >
              <div className="velorbis-customizer-header-tool-inner">
                <InlineSvg src="icons/mail" />
                <div className="velorbis-customizer-hidden-md-down">
                  {t('inquiry_button')}
                </div>
              </div>
            </div>
            <div
              className="velorbis-customizer-header-tool velorbis-customizer-header-share velorbis-customizer-header-share-desktop"
              onClick={() => dispatch(shareClicked())}
            >
              <div className="velorbis-customizer-header-tool-inner">
                <InlineSvg src="icons/share" />
                <div className="velorbis-customizer-hidden-md-down">
                  {t('share_button')}
                </div>
              </div>
            </div>
            <div className="show-768">
              <UndoButton
                onClick={() =>
                  handleUndo(
                    (id) => dispatch(undoClicked(id)),
                    () => dispatch(bikeImageReload()),
                    fullHistory,
                    undoCounter
                  )
                }
              />
            </div>
          </div>
          <div className="velorbis-customizer-header-group-social">
            <HeaderButton
              shareUrl={downloadUrl}
              type="download"
              currencyCode={currencyCode}
            />
            <HeaderButton
              shareUrl={downloadUrl}
              type="download-pdf"
              currencyCode
            />
            <HeaderButton
              shareUrl={pdfUrl}
              type="pdf"
              currencyCode={currencyCode}
            />
          </div>
        </div>
      </div>
      <div className="velorbis-customizer-header show-768">
        <div className="velorbis-header-group-mobile">
          <div
            className={`${baseModelToolclassName} velorbis-customizer-header-base-model-mobile`}
            onClick={() => dispatch(baseModelClicked())}
            title={t('base_models_title')}
          >
            <div className="velorbis-customizer-header-tool-inner">
              <div className="velorbis-customizer-flex-1">
                {getTranslation(baseModel.name)}
              </div>

              <span>&#x25BE;</span>
            </div>
          </div>
          {!isCargo && (
            <div className=" header-next-color-buttons">
              <MinusButton
                onClick={() =>
                  handleNextColor(
                    false,
                    (id) => dispatch(nextColorClicked(id)),
                    () => dispatch(bikeImageReload()),
                    false
                  )
                }
              />
              <PlusButton
                onClick={() =>
                  handleNextColor(
                    true,
                    (id) => dispatch(nextColorClicked(id)),
                    () => dispatch(bikeImageReload()),
                    false
                  )
                }
              />
            </div>
          )}
          <div className="hide-768">
            <UndoButton
              onClick={() =>
                handleUndo(
                  (id) => dispatch(undoClicked(id)),
                  () => dispatch(bikeImageReload()),
                  fullHistory,
                  undoCounter
                )
              }
            />
          </div>
        </div>

        <div className="show-768">
          <div className="velorbis-customizer-header-group-mobile">
            <div className="velorbis-customizer-header-add-to-cart-mobile">
              <BuyButton
                currencyCode={currencyCode}
                totalPrice={statement.unitPrice}
                baseModelId={baseModel.id}
              />
              <div
                className="velorbis-customizer-header-tool velorbis-customizer-header-presets"
                onClick={() => dispatch(presetsClicked())}
                title={t(isCargo ? 'select_angle' : 'presets_title')}
                style={{ marginLeft: '10px' }}
              >
                <div className="velorbis-customizer-header-tool-inner">
                  <InlineSvg src="icons/grid" />
                </div>
              </div>
            </div>
            <div className="velorbis-customizer-header-join-group-mobile">
              {getVimeoId(baseModel.id) && isDanish && (
                <div
                  className="velorbis-customizer-header-tool velorbis-customizer-header-video velorbis-customizer-header-video-mobile"
                  onClick={() => dispatch(videoClicked())}
                  title="Video"
                >
                  <div className="velorbis-customizer-header-tool-inner">
                    <InlineSvg className="youtube-icon" src="icons/youtube" />
                  </div>
                </div>
              )}
              <div
                className="velorbis-customizer-header-tool show-768 velorbis-customizer-header-settings"
                onClick={() => dispatch(settingsClicked())}
              >
                <div className="velorbis-customizer-header-tool-inner">
                  <InlineSvg src="icons/cog" />
                  <div className="velorbis-customizer-hidden-md-down">Sett</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const UndoButton = (props) => (
  <div
    className="velorbis-customizer-header-tool check specification-checbox"
    title={t('undo')}
    {...props}
  >
    <div className="velorbis-customizer-header-tool-inner">
      <InlineSvg src="icons/undo" />
      <div className="show-768 velorbis-customizer-hidden-md-down">
        {t('undo')}
      </div>
    </div>
  </div>
)

export const RedoButton = (props) => (
  <div
    className="velorbis-customizer-header-tool check specification-checbox specification-checbox__redo"
    title={t('redo')}
    {...props}
  >
    <div className="velorbis-customizer-header-tool-inner">
      <InlineSvg src="icons/redo" />
      <div className="show-768 velorbis-customizer-hidden-md-down">
        {t('redo')}
      </div>
    </div>
  </div>
)

export default Header
